import { useMemo, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { AnimatePresence, motion } from "motion/react";
import { ChevronDown, Search } from "lucide-react";
import { cn } from "@/modern/utils";
import { usePostMountFlush } from "@/modern/hooks/usePostMountFlush";
import { Switch } from "@/modern/components/ui/switch";
import type { OwnedEntity, OwnershipNotifyKey } from "../../types";
import { TabEmpty } from "../users-ui";
import { useUsersContext } from "../../context/UsersContext";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { STORE_KEYS } from "@/other/constants/StoreKeys.jsx";
import { EntityFinderTaskIcon } from "@/modern/components/entity-finder/components/EntityFinderTaskIcon";
import { SWIVU_TASK_DEFAULT_COLOR } from "@/other/configs/globalConfig.jsx";
import {
  USERS_HELPER_CLS,
  USERS_HERO_METRIC_CLS,
  USERS_META_CLS,
  USERS_TITLE_CLS,
} from "../../constants/typography";

const easeOut = [0.16, 1, 0.3, 1] as const;

const NOTIFY_LABELS: Record<OwnershipNotifyKey, string> = {
  general: "General",
  notes: "Notes",
  schedule: "Schedule",
  checklist: "Checklists",
  reminder: "Reminders",
  files: "Files",
};

const NOTIFY_HINTS: Record<OwnershipNotifyKey, string> = {
  general: "General updates",
  notes: "New and updated notes",
  schedule: "Schedule changes",
  checklist: "Checklist progress",
  reminder: "Reminders and due notices",
  files: "File uploads and changes",
};

const NOTIFY_ICONS: Record<OwnershipNotifyKey, string> = {
  general: "fa-light fa-bell",
  notes: "fa-light fa-note-sticky",
  schedule: "fa-light fa-calendar",
  checklist: "fa-light fa-list-check",
  reminder: "fa-light fa-alarm-clock",
  files: "fa-light fa-folder",
};

const ALL_CHANNELS: OwnershipNotifyKey[] = [
  "general",
  "notes",
  "schedule",
  "checklist",
  "reminder",
  "files",
];

/** Map OwnershipNotifyKey → NotifyeeBoardUser field name. */
const NOTIFY_FIELD: Record<OwnershipNotifyKey, string> = {
  general: "isGeneralUpdates",
  schedule: "isScheduleUpdates",
  notes: "isNotesUpdates",
  checklist: "isChecklistUpdates",
  reminder: "isReminderUpdates",
  files: "isFilesUpdates",
};

type EntityTab = "resource" | "task" | "client";

const ENTITY_META: Record<
  EntityTab,
  { label: string; plural: string; iconTone: string; faIcon: string }
> = {
  resource: {
    label: "Resource",
    plural: "Resources",
    iconTone: "text-muted-foreground",
    faIcon: "fa-light fa-screwdriver-wrench",
  },
  task: {
    label: "Task",
    plural: "Tasks",
    iconTone: "text-muted-foreground",
    faIcon: "fa-light fa-swap-arrows",
  },
  client: {
    label: "Client",
    plural: "Clients",
    iconTone: "text-muted-foreground",
    faIcon: "fa-light fa-building-user",
  },
};

function EntityCard({ entity, index }: { entity: OwnedEntity; index: number }) {
  const [open, setOpen] = useState(false);
  const [owned, setOwned] = useState<OwnershipNotifyKey[]>(() =>
    entity.ownedTypes.filter((key) => ALL_CHANNELS.includes(key)),
  );

  const meta = ENTITY_META[entity.entityType];
  const active = ALL_CHANNELS.filter((key) => owned.includes(key));

  function toggle(key: OwnershipNotifyKey, on: boolean) {
    setOwned((prev) => {
      if (on) return prev.includes(key) ? prev : [...prev, key];
      return prev.filter((k) => k !== key);
    });
    entity.record?.set(NOTIFY_FIELD[key], on);
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: Math.min(index, 8) * 0.04, ease: easeOut }}
      className={cn(
        "relative rounded-lg border overflow-hidden transition-colors duration-150 ease-out",
        open
          ? "border-border bg-card shadow-sm"
          : "border-border/60 bg-card hover:border-border hover:bg-muted/20",
      )}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={cn(
          "w-full text-left px-3.5",
          open || active.length > 0 ? "py-3" : "py-2.5",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
        )}
      >
        <div className="flex items-center gap-3">
          {entity.entityType === "task" ? (
            <EntityFinderTaskIcon color={entity.color ?? SWIVU_TASK_DEFAULT_COLOR} />
          ) : (
            <div
              className={cn(
                "flex size-8 shrink-0 items-center justify-center rounded-lg",
                "bg-background ring-1 ring-border/50",
                meta.iconTone,
              )}
            >
              <i className={cn(meta.faIcon, "text-base")} aria-hidden />
            </div>
          )}

          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className={cn(
                  USERS_TITLE_CLS,
                  "font-normal truncate leading-none tracking-tight",
                  entity.archived && "line-through opacity-60",
                )}>
                  {entity.name}
                </p>
              </div>
              <div className="shrink-0 flex items-center gap-2">
                <p className={cn(USERS_META_CLS, "text-muted-foreground")}>
                  {active.length}/{ALL_CHANNELS.length}
                </p>
                <ChevronDown
                  className={cn(
                    "size-3.5 text-muted-foreground transition-transform duration-200",
                    open && "rotate-180",
                  )}
                />
              </div>
            </div>

            {!open &&
              (active.length > 0 ? (
                <div className="mt-2.5 flex flex-wrap gap-1.5">
                  {active.map((key) => (
                    <span
                      key={key}
                      className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md bg-muted/50 border border-border/60 text-sm font-normal text-foreground/90"
                    >
                      <i className={cn(NOTIFY_ICONS[key], "text-sm text-muted-foreground")} aria-hidden />
                      {NOTIFY_LABELS[key]}
                    </span>
                  ))}
                </div>
              ) : (
                <p className={cn("mt-2.5", USERS_HELPER_CLS)}>
                  No notification channels enabled.
                </p>
              ))}
          </div>
        </div>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: easeOut }}
            className="overflow-hidden"
          >
            <div className="border-t border-border/50 mx-3.5 mb-3 pt-2.5 space-y-1">
              <p className={cn(USERS_HELPER_CLS, "px-0.5 mb-1.5")}>
                Notification channels
              </p>
              {ALL_CHANNELS.map((key) => {
                const on = owned.includes(key);
                return (
                  <div
                    key={key}
                    className="flex items-center justify-between gap-3 rounded-md bg-background/60 border border-border/40 px-2.5 py-2.5"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-muted/50 text-muted-foreground">
                        <i className={cn(NOTIFY_ICONS[key], "text-sm")} aria-hidden />
                      </span>
                      <div className="min-w-0">
                        <p className={cn(USERS_TITLE_CLS, "font-normal leading-none")}>
                          {NOTIFY_LABELS[key]}
                        </p>
                        <p className={cn("mt-0.5 truncate", USERS_HELPER_CLS)}>
                          {NOTIFY_HINTS[key]}
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={on}
                      onCheckedChange={(checked) => toggle(key, checked)}
                      aria-label={`${NOTIFY_LABELS[key]} for ${entity.name}`}
                    />
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function OwnershipTab({ user }: { user: any }) {
  const { project, tabScrollRef, showArchived } = useUsersContext();
  const [query, setQuery] = useState("");

  const { version: notifyVersion } = useSwivuStore({ storeId: STORE_KEYS.notifyeeBoardUsers });

  const entities = useMemo<OwnedEntity[]>(() => {
    if (!project) return [];
    const records = project.notfiyeeBoardUserStore.query((n) => n.boardUserId === user.id);

    return records
      .map((n): OwnedEntity | null => {
        let entityType: "resource" | "task" | "client";
        let entityName: string;
        let entityArchived: boolean = false;
        let entityColor: string | undefined;

        if (n.resourceId) {
          entityType = "resource";
          const rec = project.resourceStore.getById(n.resourceId);
          if (!rec && !showArchived) return null;
          entityName = rec?.name ?? `Resource #${n.resourceId}`;
          entityArchived = rec?.archived ?? false;
        } else if (n.taskId) {
          entityType = "task";
          const rec = project.extraTaskStore.getById(n.taskId);
          // When store hides archived records, rec is null — skip if showArchived is off
          if (!rec && !showArchived) return null;
          entityName = rec?.name ?? `Task #${n.taskId}`;
          entityArchived = rec?.archived ?? false;
          entityColor = rec?.color ?? undefined;
        } else if (n.clientId) {
          entityType = "client";
          const rec = project.clientsStore.getById(n.clientId);
          if (!rec && !showArchived) return null;
          entityName = rec?.name ?? `Client #${n.clientId}`;
          entityArchived = rec?.archived ?? false;
        } else {
          return null;
        }

        const ownedTypes: OwnershipNotifyKey[] = ALL_CHANNELS.filter(
          (key) => (n as any)[NOTIFY_FIELD[key]] === true,
        );

        return { id: n.id, name: entityName, entityType, ownedTypes, archived: entityArchived, color: entityColor, record: n };
      })
      .filter(Boolean) as OwnedEntity[];
  }, [user.id, notifyVersion, project, showArchived]);

  const counts = useMemo(
    () => ({
      total: entities.length,
      resource: entities.filter((e) => e.entityType === "resource").length,
      task: entities.filter((e) => e.entityType === "task").length,
      client: entities.filter((e) => e.entityType === "client").length,
    }),
    [entities],
  );

  const tabs = ["resource", "task", "client"] as EntityTab[];
  const defaultTab: EntityTab = "resource";
  const [active, setActive] = useState<EntityTab>(defaultTab);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return entities.filter((e) => {
      if (e.entityType !== active) return false;
      if (!showArchived && e.archived) return false;
      if (q && !e.name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [entities, query, active, showArchived]);

  usePostMountFlush();

  /** Virtualize the entity list — cards can expand so we use measureElement. */
  const virtualizer = useVirtualizer({
    count: filtered.length,
    getScrollElement: () => tabScrollRef.current,
    estimateSize: () => 80,
    overscan: 5,
  });

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-border/70 bg-card px-4 py-3.5 shadow-sm">
        <p className={cn(USERS_HELPER_CLS, "tracking-wide")}>
          Owned entities
        </p>
        <div className="mt-2 flex items-end gap-2.5 flex-wrap">
          <span className={cn(USERS_HERO_METRIC_CLS, "text-2xl text-foreground")}>
            {counts.total}
          </span>
        </div>
      </div>

      <div>
        <nav className="border-b border-border/70">
          <div className="flex gap-5 overflow-x-auto overflow-y-hidden">
              {tabs.map((id) => {
              const on = id === active;
              const meta = ENTITY_META[id];
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setActive(id)}
                  className={cn(
                    "inline-flex items-center gap-1.5 pb-2.5 pt-0.5 text-base transition-colors border-b-2 -mb-px whitespace-nowrap font-normal",
                    on
                      ? "text-foreground border-primary"
                      : "text-muted-foreground hover:text-foreground border-transparent",
                  )}
                >
                  <i className={cn(meta.faIcon, "shrink-0 opacity-80 text-base")} aria-hidden />
                  {meta.plural}
                  <span
                    className={cn(
                      "inline-flex items-center justify-center h-6 min-w-6 px-1.5 rounded-full tabular-nums text-sm font-medium leading-none",
                      on
                        ? "bg-muted text-foreground"
                        : "bg-muted/50 text-muted-foreground",
                    )}
                  >
                    {counts[id]}
                  </span>
                </button>
              );
            })}
          </div>
        </nav>

        <div className="pt-4 space-y-3">
          <div
            className={cn(
              "flex items-center gap-0.5 rounded-xl",
              "bg-card/80 border border-border/60 shadow-sm",
              "pl-2.5 pr-1 py-1",
              "transition-[border-color,box-shadow,background-color] duration-150 ease-out",
              "focus-within:border-primary/40 focus-within:ring-1 focus-within:ring-primary/35",
              "focus-within:bg-card",
            )}
          >
            <Search className="size-3.5 shrink-0 text-muted-foreground/80 pointer-events-none" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${ENTITY_META[active].plural.toLowerCase()}…`}
              className={cn(
                "min-w-0 flex-1 bg-transparent border-0 px-2 py-1.5",
                "text-base font-normal text-foreground placeholder:text-muted-foreground/70",
                "focus:outline-none focus:ring-0",
              )}
            />
          </div>

          {filtered.length === 0 ? (
            <TabEmpty
              icon={ENTITY_META[active].faIcon}
              message={counts[active] === 0
                ? `No ${ENTITY_META[active].plural.toLowerCase()} assigned.`
                : `No ${ENTITY_META[active].plural.toLowerCase()} match this search.`}
              hint={counts[active] === 0
                ? `Add this user as an owner on a ${ENTITY_META[active].label.toLowerCase()} to see it here.`
                : undefined}
            />
          ) : (
            <div style={{ height: `${virtualizer.getTotalSize()}px`, position: "relative" }}>
              {virtualizer.getVirtualItems().map((vItem) => (
                <div
                  key={vItem.key}
                  data-index={vItem.index}
                  ref={virtualizer.measureElement}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    transform: `translateY(${vItem.start}px)`,
                  }}
                >
                  <div className="pb-1.5">
                    <EntityCard entity={filtered[vItem.index]} index={vItem.index} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
